import { createElement } from 'react';
import { captureException } from '@sentry/react';
import { BiDuplicate } from 'react-icons/bi';
import { TbArchive, TbArchiveOff, TbLock, TbLockOpen, TbStarFilled, TbStarOff } from 'react-icons/tb';
import { StateCreator } from 'zustand';

import { teamService } from '@/services';
import { toast } from '@/shared/components/toasts/PlatformToast';
import {
    appendEntity,
    createSingleFlightKey,
    mergeEntity,
    mergeEntityById,
    removeEntityById,
    replaceEntityById,
    replaceOptimisticEntityById,
    runSingleFlight,
    upsertEntityById,
} from '@/stores/helpers/collections';
import { Team } from '@/types';

import { TeamActions, TeamState } from '../types';

export const createTeamActions: StateCreator<TeamState & TeamActions, [['zustand/immer', never]], [], TeamActions> = (
    set,
    get,
    store,
) => ({
    // Core
    fetchTeams: async (params) => {
        set({ loading: true });

        try {
            const teams = await teamService.list(params);

            set({ teams, loading: false, error: undefined });
        } catch (error) {
            captureException(error);

            set({ loading: false, error: 'Failed to load teams' });
        }
    },
    fetchTeamById: async (id) => {
        set((state) => {
            state.loadingTeam = true;
            state.error = undefined;
        });

        try {
            const team = await teamService.getById(id);

            set({ selectedTeam: team, loadingTeam: false });
        } catch (error) {
            captureException(error);

            set((state) => {
                state.error = 'Failed to fetch team detail';
                state.loadingTeam = false;
            });
        }
    },
    clearSelectedTeam: () => {
        set((state) => {
            state.selectedTeam = null;
        });
    },
    createTeam: async (input) =>
        runSingleFlight(createSingleFlightKey('teams:create', input), async () => {
            const tempId = `temp-${Date.now()}`;
            const optimistic = { id: tempId, ...input } as Team;

            set({ teams: appendEntity(get().teams, optimistic) });

            try {
                const team = await teamService.create(input);

                set({ teams: replaceOptimisticEntityById(get().teams, tempId, team) });

                return team;
            } catch (error) {
                captureException(error);

                set({ teams: removeEntityById(get().teams, tempId), error: 'Failed to create team' });
            }
        }),
    joinTeam: async (id) => {
        const prev = get().teams;

        set({
            teams: get().teams.map((team) =>
                team.id === id
                    ? {
                          ...team,
                          joined: true,
                          canJoin: false,
                          teammateCount: (team.teammateCount ?? team.teammates?.length ?? 0) + 1,
                      }
                    : team,
            ),
        });

        try {
            await teamService.joinWorkspaceVisibleTeam(id);

            await get().fetchTeams();
        } catch (error) {
            captureException(error);

            set({ teams: prev, error: 'Failed to join team' });
        }
    },
    updateTeam: async (id, updates) => {
        const prev = get().teams.find((f) => f.id === id);
        if (!prev) {
            return;
        }

        set({
            teams: mergeEntityById(get().teams, id, updates),
            selectedTeam:
                get().selectedTeam?.id === id ? mergeEntity(get().selectedTeam!, updates) : get().selectedTeam,
        });

        try {
            const team = await teamService.update(id, updates);

            set({
                teams: mergeEntityById(get().teams, id, team),
                selectedTeam:
                    get().selectedTeam?.id === id ? mergeEntity(get().selectedTeam!, team) : get().selectedTeam,
            });
        } catch (error) {
            captureException(error);

            set({
                teams: replaceEntityById(get().teams, id, prev),
                selectedTeam: get().selectedTeam?.id === id ? prev : get().selectedTeam,
                error: 'Failed to update team',
            });
        }
    },
    deleteTeam: async (id) => {
        const prev = get().teams;

        set({
            teams: removeEntityById(get().teams, id),
            selectedTeam: get().selectedTeam?.id === id ? null : get().selectedTeam,
        });

        try {
            await teamService.delete(id);
        } catch (error) {
            captureException(error);

            set({ teams: prev, error: 'Failed to delete team' });
        }
    },
    // - Aliases
    duplicateTeam: async (id) => {
        const prev = get().teams;

        if (!prev) {
            return;
        }

        try {
            const duplicated = await teamService.duplicate(id);

            set({ teams: upsertEntityById(get().teams, duplicated, { prepend: true }) });

            toast('Team Duplicated', {
                icon: createElement(BiDuplicate, { size: '1rem' }),
                description: duplicated.name,
                resource: { id: duplicated.id, type: 'team' },
            });

            return duplicated;
        } catch (error) {
            captureException(error);

            set({ teams: prev, error: 'Failed to duplicate team' });

            toast.error('Failed to duplicate team');
        }
    },
    toggleTeamFavorite: async (id: string, favorited: boolean) => {
        const prev = get().teams.find((f) => f.id === id);

        if (!prev) {
            return;
        }

        set((state) => {
            const team = state.teams.find((f) => f.id === id);
            if (team) {
                team.favorited = favorited;
            }

            // Also update selectedTeam if it matches
            if (state.selectedTeam?.id === id) {
                state.selectedTeam.favorited = favorited;
            }
        });

        try {
            await teamService.setFavorite(id, favorited);

            toast(`${favorited ? 'Added to' : 'Removed from'} Favorites`, {
                icon: createElement(favorited ? TbStarFilled : TbStarOff, { size: '1rem' }),
                description: prev.name,
                resource: { id, type: 'team' },
            });
        } catch (error) {
            captureException(error);

            set((state) => {
                const team = state.teams.find((f) => f.id === id);
                if (team) {
                    team.favorited = prev.favorited;
                }

                // Revert selectedTeam favorited if it matches
                if (state.selectedTeam?.id === id) {
                    state.selectedTeam.favorited = prev.favorited;
                }

                state.error = 'Failed to update favorite';
            });
        }
    },
    toggleTeamLock: async (id, locked) => {
        const prev = get().teams.find((f) => f.id === id);

        if (!prev) {
            return;
        }

        set((state) => {
            const team = state.teams.find((f) => f.id === id);
            if (team) {
                team.locked = locked;
            }

            // Also update selectedTeam if it matches
            if (state.selectedTeam?.id === id) {
                state.selectedTeam.locked = locked;
            }
        });

        try {
            await teamService.setLocked(id, locked);

            toast(`Team ${locked ? 'Locked' : 'Unlocked'}`, {
                icon: createElement(locked ? TbLock : TbLockOpen, { size: '1rem' }),
                description: prev.name,
                resource: { id, type: 'team' },
            });
        } catch (error) {
            captureException(error);

            set((state) => {
                const team = state.teams.find((f) => f.id === id);
                if (team) {
                    team.locked = prev.locked;
                }

                // Revert selectedTeam locked if it matches
                if (state.selectedTeam?.id === id) {
                    state.selectedTeam.locked = prev.locked;
                }

                state.error = 'Failed to update favorite';
            });
        }
    },
    toggleTeamArchive: async (id, archived) => {
        const prev = get().teams.find((f) => f.id === id);

        if (!prev) {
            return;
        }

        set((state) => {
            const team = state.teams.find((f) => f.id === id);
            if (team) {
                team.archived = archived;
            }

            // Also update selectedTeam if it matches
            if (state.selectedTeam?.id === id) {
                state.selectedTeam.archived = archived;
            }
        });

        try {
            await teamService.setArchived(id, archived);

            toast(`Team ${archived ? 'Archived' : 'Unarchived'}`, {
                icon: createElement(archived ? TbArchive : TbArchiveOff, { size: '1rem' }),
                description: prev.name,
                resource: { id, type: 'team' },
            });
        } catch (error) {
            captureException(error);

            set((state) => {
                const team = state.teams.find((f) => f.id === id);
                if (team) {
                    team.archived = prev.archived;
                }

                // Revert selectedTeam archived if it matches
                if (state.selectedTeam?.id === id) {
                    state.selectedTeam.archived = prev.archived;
                }

                state.error = 'Failed to update favorite';
            });
        }
    },
});
